import React, { useState, useMemo, useEffect } from 'react';
import { useIntl } from 'react-intl';
import cn from 'classnames';
import { useSelector, useDispatch } from 'react-redux';

import Icon from '../../../Icon';
import Button from '../../../Button';
import Popup from '../../../common/Popup';
import Loader from '../../../common/Loader';
import Checkbox from '../../../common/Checkbox';
import BackButton from '../../../common/BackButton';
import RadioGroup from '../../../common/RadioGroup';
import PaymentAccountFormLoader from '../../../common/PaymentAccountFormLoader';

import { COLORS } from '../../../../constants/styles';
import { UI_POPUP_TYPES } from '../../../../constants/props';

import useDefaultCard from '../../../../hooks/payments/account-form/useDefaultCard';

import { formatCurrencyUSD } from '../../../../utils/currency';

import { consts as popupConsts } from '../../../../redux/ui/popup';

import { actions as accountVaultsFormActions } from '../../../../redux/programs/accountVaultsForm';
import { actions as appointmentAccountFormPaymentActions } from '../../../../redux/appointmnents/requests/appointment-account-form-payment-req';
import { appointmentPaymentInfoModalActions } from '../../../../redux/appointmentForm/appointment-payment-info-modal';

import { selectPaymentOrderAmount } from '../../../../redux/healthboard/selectors';
import { selectAccountVaultsListLoading } from '../../../../redux/programs/selectors';
import { selectAppointmentAccountFormPaymentSending } from '../../../../redux/appointmnents/selectors';
import {
  selectAppointmentPaymentInfoModalOpened,
  selectAppointmentPaymentInfoModalProps,
} from '../../../../redux/appointmentForm/selectors';

import styles from './styles.module.styl';

const labelStyle = {
  fontSize: '12px',
  fontWeight: 'normal',
  lineHeight: 'normal',
  opacity: 1,
  color: COLORS.fontRegular,
};

const AppointmentPaymentInfoPopup = () => {
  const { formatMessage } = useIntl();

  const dispatch = useDispatch();

  const [cardAgreement, setCardAgreement] = useState(false);

  const [selectedCardId, setSelectedCardId] = useState();

  const isOpened = useSelector(selectAppointmentPaymentInfoModalOpened);

  const modalProps = useSelector(selectAppointmentPaymentInfoModalProps);

  const areCardsLoading = useSelector(selectAccountVaultsListLoading);

  const appointmentAccountFormPaymentSending = useSelector(
    selectAppointmentAccountFormPaymentSending,
  );

  const price = useSelector(selectPaymentOrderAmount);

  const { defaultCard, mappedCards } = useDefaultCard();

  const addPaymentMethodTitle = formatMessage({
    id: 'paya.payment.addNewPaymentMethod',
  });

  const formDescription = formatMessage({
    id: 'profile.payment.addCardForFutureUse',
  });

  const { userId, locationId, paymentOrderId } = modalProps || {};

  const isSubmitDisabled = !selectedCardId || !cardAgreement;

  const amount = useMemo(() => formatCurrencyUSD(price), [price]);

  const handleOnChange = id => setSelectedCardId(id);

  const onClose = () => {
    dispatch(appointmentPaymentInfoModalActions.close());
  };

  const handleAddNewCard = () => {
    dispatch(
      accountVaultsFormActions.send({
        userId,
        locationId,
      }),
    );

    dispatch({
      type: popupConsts.SHOW,
      payload: {
        contentType: UI_POPUP_TYPES.PAYA_ACCOUNT_FORM,
        contentOptions: {
          noCards: false,
        },
      },
    });
  };

  const handleSubmitPayment = () => {
    dispatch(
      appointmentAccountFormPaymentActions.send({
        accountVaultId: selectedCardId,
        paymentOrderId,
      }),
    );
  };

  useEffect(() => {
    if (defaultCard) {
      setSelectedCardId(defaultCard.id);
    }
  }, [defaultCard]);

  useEffect(() => {
    if (!isOpened) {
      onClose();
      dispatch(appointmentAccountFormPaymentActions.clear());
      setCardAgreement(false);
      setSelectedCardId(undefined);
    }
  }, [isOpened]);

  return (
    <Popup opened={isOpened} withHeading={false}>
      <div className={styles.root}>
        <BackButton
          onBack={onClose}
          className={styles.backBtn}
          minWidth={100}
        />
        <div
          className={cn(styles.content, {
            [styles.loading]: appointmentAccountFormPaymentSending,
          })}
        >
          <div className={styles.mainTitle}>
            {formatMessage({ id: 'appointment.list.card.payment' })}
          </div>

          <div className={styles.headerDescription}>{formDescription}</div>
          <div className={styles.description}>
            <div>
              <div className={styles.cellTitle}>
                {formatMessage({ id: 'paya.payment.amount' })}
              </div>
              <div className={styles.cellText}>{amount}</div>
            </div>
          </div>
          <div className={styles.card}>
            <div className={styles.title}>
              {formatMessage({ id: 'paya.payment.card' })}
            </div>
            {areCardsLoading && !mappedCards.length ? (
              <Loader />
            ) : (
              <RadioGroup
                name="cardsradioBtn"
                value={selectedCardId}
                onChange={handleOnChange}
                radios={mappedCards}
              />
            )}
            <div className={styles.btnAdd}>
              <button type="button" onClick={handleAddNewCard}>
                <Icon name="add-text" size={16} className={styles.icon} />
                <span>{addPaymentMethodTitle}</span>
              </button>
            </div>
            <Checkbox
              size={16}
              id="cardAgreement"
              name="cardAgreement"
              checked={cardAgreement}
              labelStyle={labelStyle}
              label={formatMessage({ id: 'paya.payment.cardAgreement' })}
              onChange={() => setCardAgreement(prev => !prev)}
            />
            <div className={styles.transactionDescription}>
              {formatMessage({ id: 'paya.payment.transactionDescription' })}
            </div>
          </div>
          <div className={styles.btnSubmit}>
            <Button
              disabled={isSubmitDisabled}
              color="primary"
              onClick={handleSubmitPayment}
            >
              {formatMessage({ id: 'paya.payment.submitPayment' })}
            </Button>
          </div>
        </div>
      </div>
      {appointmentAccountFormPaymentSending && (
        <div className={styles.loaderWrapper}>
          <PaymentAccountFormLoader />
        </div>
      )}
    </Popup>
  );
};

export default AppointmentPaymentInfoPopup;



// WEBPACK FOOTER //
// ./src/components/AppointmentForm/Components/AppointmentPaymentInfoPopup/index.jsx